import { Cancel, Edit, Tune as TuneIcon } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  DialogActions,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getCompanyConfig } from 'lib/api/company';
import { getDocumentWithExtensionData } from 'lib/api/extension/factoring';
import { getCompanyFunds } from 'lib/api/funds';
import {
  createFactoringReSell,
  getDocumentsAvailableForReSelling,
  getFactoringReSell,
  replaceResellDocument,
  updateFactoringReSell,
} from 'lib/api/reSell/factoring';
import { CompanyDocumentResell } from 'lib/api/reSell/factoring/types';
import {
  AutocompleteInput,
  DateInput,
  DecimalInput,
  MoneyInput,
} from 'lib/common/Inputs';
import CustomButton from 'lib/designSystem/Button';
import TableCard from 'lib/designSystem/Card/TableCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { isDateAWeekDay } from 'lib/functions/dates';
import { calculateFundRateCost } from 'lib/functions/fundCostRateCalculation';
import {
  formatDate,
  showAlert,
  validatePermissions,
} from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { useFactoringResell } from '../../../../context';
import RevertResell from '../../../RevertResell';
import DocumentEditor from './components/DocumentEditor';
import DocumentInfoCard from './components/DocumentInfoCard';
import ResellSummaryCard from './components/ResellSummaryCard';

interface ResellData {
  id?: string;
  rate: string;
  operationCostFixed: number;
  operationCostVariable: number;
  amount: number;
  advance: number;
  arrearRate: number;
  days: number;
  fund: { id: string; name: string } | null;
  rateCost: number;
  operationCostRateVariable: string;
  startDate: string;
  endDate: string;
  documentId: string | null;
  observation: string;
  fundId: string | null;
  status?: string;
  ownCollectionAt?: string;
  buybackAt?: string;
  FundPayment?: { id: number; amount: number }[];
}

interface DocumentData {
  id: string;
  folio: string;
  businessName: string;
  stakeholderName: string;
  amount: number;
  amountConciliated: number;
  totalRemaining: number;
  startingPaymentDate: string;
  paymentDate: string;
  documentRemainingAmount: number;
}

interface FundData {
  id: string;
  name: string;
}

interface DocumentFormParametersProps {
  document: DocumentData;
  resellData: ResellData;
  setResellData: (resell: ResellData) => void;
  canModifiyParams: boolean;
  funds: FundData[];
  canForcePaymentDate: boolean;
  onToggleEditMode: () => void;
}

const DocumentFormParameters = (props: DocumentFormParametersProps) => {
  const {
    document,
    resellData,
    setResellData,
    canModifiyParams,
    canForcePaymentDate,
    onToggleEditMode,
  } = props;
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [funds, setFunds] = useState<FundData[]>([]);
  const [fundsCostDict, setFundsCostDict] = useState<Record<string, number>>(
    {},
  );
  const [loading, setLoading] = useState(false);
  const [fundCalculationMethodHash, setFundCalculationMethodHash] = useState<
    Record<string, string | undefined>
  >({});
  const calculateDays = (start: Date, end: Date) => {
    const days = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
    );
    return days;
  };
  const [days, setDays] = useState(
    calculateDays(new Date(resellData.startDate), new Date(resellData.endDate)),
  );

  const dateValidators = (date: Date, type: 'startDate' | 'endDate') => {
    if (canForcePaymentDate) {
      const startDate =
        type === 'startDate' ? date : new Date(resellData.startDate);
      const endDate = type === 'endDate' ? date : new Date(resellData.endDate);
      const days = calculateDays(startDate, endDate);
      if (days <= 0) {
        alert(
          'La fecha de vencimiento no puede ser menor a la fecha de liberación',
        );
        return false;
      }
      return days;
    }
    if (!isDateAWeekDay(date) && type === 'endDate') {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento seleccionada no es un día hábil.',
      );
    }
    if (date < new Date() && type === 'endDate') {
      alert('La fecha de vencimiento no puede ser menor a la fecha actual');
      return false;
    }
    const startDate =
      type === 'startDate' ? date : new Date(resellData.startDate);
    const endDate = type === 'endDate' ? date : new Date(resellData.endDate);
    const daysCalc = calculateDays(startDate, endDate);
    if (daysCalc <= 0) {
      alert(
        'La fecha de vencimiento no puede ser menor a la fecha de liberación',
      );
      return false;
    }
    return daysCalc;
  };

  const handleChangeDate = (value: string, type: 'startDate' | 'endDate') => {
    const validDays = dateValidators(new Date(value), type);
    if (!validDays) return;
    setDays(validDays);

    const rateCost = calculateFundRateCost(
      resellData.amount,
      resellData.advance,
      resellData.operationCostFixed,
      parseFloat(resellData.rate),
      validDays,
      fundCalculationMethodHash,
      resellData.fund?.id,
    );

    const updatedData = {
      ...resellData,
      [type]: value,
      rateCost,
    };

    setResellData(updatedData);
  };

  // Handlers simplificados para los inputs tipados
  const handleChangeAmountNew = (value: number | null) => {
    const amount = value || 0;
    if (amount > document.amount) {
      alert('El monto a financiar no puede ser mayor al monto del documento');
      return;
    }
    const rateCost = calculateFundRateCost(
      amount,
      resellData.advance,
      resellData.operationCostFixed,
      parseFloat(resellData.rate),
      days,
      fundCalculationMethodHash,
      resellData.fund?.id,
    );
    setResellData({
      ...resellData,
      amount,
      rateCost,
    });
  };

  const handleChangeRateNew = (
    value: number | null,
    type: 'advance' | 'arrearRate' | 'rate',
  ) => {
    const numValue = value || 0;
    const rateCost = calculateFundRateCost(
      resellData.amount,
      type === 'advance' ? numValue : resellData.advance,
      resellData.operationCostFixed,
      type === 'rate' ? numValue : parseFloat(resellData.rate),
      days,
      fundCalculationMethodHash,
      resellData.fund?.id,
    );
    setResellData({
      ...resellData,
      [type]: type === 'rate' ? String(numValue) : numValue,
      rateCost,
    });
  };

  const handleChangeFundNew = (value: FundData | null) => {
    const rate = value ? fundsCostDict[value.id] : 0;
    const rateCost = calculateFundRateCost(
      resellData.amount,
      resellData.advance,
      resellData.operationCostFixed,
      rate,
      days,
      fundCalculationMethodHash,
      value?.id,
    );
    setResellData({
      ...resellData,
      fundId: value ? value.id : null,
      fund: value ? value : null,
      rateCost,
      rate: String(rate),
    });
  };

  const canEditByStatus =
    resellData.status === 'En revisión' || resellData.status === 'Activa';

  const canEditByUser = validatePermissions(user, 'financing_fundSale_edit');

  const fetchFunds = async () => {
    setLoading(true);
    try {
      const fundsResponse = await getCompanyFunds(dispatch);
      const fundsCostDictLocal = fundsResponse.reduce(
        (acc: Record<string, number>, fund: { id: string; cost: number }) => {
          acc[fund.id] = fund.cost;
          return acc;
        },
        {},
      );
      const fundCalculationMethodHashLocal = fundsResponse.reduce(
        (
          acc: Record<string, string | undefined>,
          fund: {
            id: string;
            FundCompanyRelation?: { calculationMethod?: string }[];
          },
        ) => {
          const calculationMethod =
            fund.FundCompanyRelation?.[0]?.calculationMethod || 'SIMPLE';

          if (fund.id) {
            acc[fund.id] = calculationMethod;
          }
          return acc;
        },
        {} as Record<string, string | undefined>,
      );
      setFundsCostDict(fundsCostDictLocal);
      setFundCalculationMethodHash(fundCalculationMethodHashLocal);
      setFunds(fundsResponse);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    void fetchFunds();
  }, []);

  if (loading) {
    return <CenteredCircularProgress type="dialog" />;
  }

  return (
    <div style={{ margin: '8px 0px' }}>
      <TableCard size="small" enabledEffects={false} sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="stretch">
          <Grid
            item
            xs={12}
            sm={6}
            sx={{ display: 'flex', flexDirection: 'column', pr: 2 }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ width: '100%', mb: 2 }}
            >
              <Box
                sx={{
                  backgroundColor: 'primary.main',
                  borderRadius: '50%',
                  p: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                }}
              >
                <TuneIcon sx={{ fontSize: '1rem' }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '1rem',
                  lineHeight: 1.1,
                  flex: 1,
                }}
              >
                Parámetros venta
              </Typography>
              {canEditByStatus && canEditByUser && (
                <IconButton size="small" onClick={onToggleEditMode}>
                  {canModifiyParams ? (
                    <Cancel fontSize="small" />
                  ) : (
                    <Edit fontSize="small" />
                  )}
                </IconButton>
              )}
            </Stack>
            {/* Contenido del formulario */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DateInput
                  label="Fecha de inicio"
                  value={resellData.startDate}
                  disabled={!canModifiyParams}
                  onChange={(value) => handleChangeDate(value, 'startDate')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateInput
                  label="Fecha de vencimiento"
                  value={resellData.endDate}
                  disabled={!canModifiyParams}
                  onChange={(value) => handleChangeDate(value, 'endDate')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MoneyInput
                  label="Monto a financiar"
                  value={resellData.amount}
                  onChange={(value) => handleChangeAmountNew(value)}
                  disabled={!canModifiyParams}
                  max={document.amount}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DecimalInput
                  label="Anticipo"
                  value={resellData.advance}
                  onChange={(value) => handleChangeRateNew(value, 'advance')}
                  disabled={!canModifiyParams}
                  isPercentage
                />
              </Grid>
              {/* Fila 3: Fondo */}
              <Grid item xs={12}>
                <AutocompleteInput
                  options={funds}
                  value={funds.find((x) => x.id === resellData.fundId) || null}
                  getOptionLabel={(option) => `${option.name} | ${option.id}`}
                  label="Fondo"
                  onChange={(value) => handleChangeFundNew(value)}
                  disabled={!canModifiyParams}
                />
              </Grid>
              {/* Fila 4: Tasa - Tasa Mora - Comisión */}
              <Grid item xs={12} sm={4}>
                <DecimalInput
                  label="Tasa"
                  value={parseFloat(resellData.rate) || null}
                  onChange={(value) => handleChangeRateNew(value, 'rate')}
                  disabled={!canModifiyParams}
                  isPercentage
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DecimalInput
                  label="Tasa Mora"
                  value={resellData.arrearRate}
                  onChange={(value) => handleChangeRateNew(value, 'arrearRate')}
                  disabled={!canModifiyParams}
                  isPercentage
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <MoneyInput
                  label="Comisión"
                  value={resellData.operationCostFixed}
                  onChange={(value) =>
                    setResellData({
                      ...resellData,
                      operationCostFixed: value || 0,
                    })
                  }
                  disabled={!canModifiyParams}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
            <Box
              sx={{
                flex: 1,
                pl: 2,
                borderLeft: '1px solid',
                borderColor: 'divider',
              }}
            >
              <ResellSummaryCard
                amount={resellData.amount}
                advance={resellData.advance}
                days={days}
                rateCost={resellData.rateCost}
                operationCostFixed={resellData.operationCostFixed}
                operationCostVariable={resellData.operationCostVariable}
              />
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ mt: 3 }}>
          <Typography variant="body1" fontWeight="bold" margin={'8px 0px'}>
            Observaciones
          </Typography>
          <TextField
            fullWidth
            disabled={!canModifiyParams}
            multiline
            rows={4}
            value={resellData.observation}
            onChange={(e) =>
              setResellData({ ...resellData, observation: e.target.value })
            }
          />
        </Box>
      </TableCard>
    </div>
  );
};

// Helper para convertir resell del context a ResellData local
const convertToResellData = (resell: CompanyDocumentResell): ResellData => {
  return {
    id: String(resell.id),
    rate: String(resell.rate),
    operationCostFixed: resell.operationCostFixed,
    operationCostVariable: resell.operationCostVariable,
    amount: resell.amount,
    advance: resell.advance,
    arrearRate: resell.arrearRate || 0,
    days: 0,
    fund: resell.fund,
    rateCost: resell.rateCost,
    operationCostRateVariable: String(resell.operationCostRateVariable || ''),
    startDate: formatDate(new Date(resell.startDate)),
    endDate: formatDate(new Date(resell.dueDate)),
    documentId: resell.documentId,
    observation: resell.observation || '',
    fundId: resell.fundId,
    status: resell.status,
    ownCollectionAt: resell.ownCollectionAt
      ? String(resell.ownCollectionAt)
      : undefined,
    buybackAt: resell.buybackAt ? String(resell.buybackAt) : undefined,
    FundPayment: resell.FundPayment,
  };
};

const ResellMain: React.FC = () => {
  const {
    originalResell,
    resell,
    setOpenNewResell,
    fetchResellings,
    edit,
    triggerSearch,
    pagination,
  } = useFactoringResell();

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<DocumentData[]>([]);
  const [document, setDocument] = useState<DocumentData | null>(null);
  const [searched, setSearched] = useState(false);
  const [funds, setFunds] = useState<FundData[]>([]);
  const [canModifyParams, setCanModifyParams] = useState(!edit);
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [canForcePaymentDate, setCanForcePaymentDate] = useState(false);
  const [localResellToRevert, setLocalResellToRevert] = useState<string | null>(
    null,
  );
  const [openRevert, setOpenRevert] = useState(false);
  const [openDocumentEditor, setOpenDocumentEditor] = useState(false);

  // Local state para formulario
  const [resellData, setResellData] = useState<ResellData>(
    convertToResellData(resell!),
  );
  // Estado para guardar valores originales cuando se inicia la edición
  const [originalResellData, setOriginalResellData] =
    useState<ResellData | null>(null);

  const canEditByStatus =
    resellData.status === 'En revisión' || resellData.status === 'Activa';
  const canEditByUser = validatePermissions(user, 'financing_fundSale_edit');

  // Handler para manejar el cambio de modo edición
  const handleToggleEditMode = () => {
    if (canModifyParams) {
      // Cancelando: restaurar valores originales
      if (originalResellData) {
        setResellData(originalResellData);
        setOriginalResellData(null);
      }
    } else {
      // Iniciando edición: guardar valores originales
      setOriginalResellData({ ...resellData });
    }
    setCanModifyParams(!canModifyParams);
  };

  const validParams = () => {
    return true;
  };

  const handleChangeDocument = (
    event: React.SyntheticEvent,
    newValue: DocumentData | null,
  ) => {
    setDocument(newValue);
    setResellData({ ...resellData, documentId: newValue?.id || null });
  };

  const handleSearchData = async () => {
    setLoading(true);
    try {
      if (!document) return;
      const response = await getDocumentWithExtensionData(
        dispatch,
        document.id,
      );
      setDocument(response);
      const fundsResponse = await getCompanyFunds(dispatch);
      setFunds(fundsResponse);
      setSearched(true);
    } catch (error) {
      // Error handling
    }
    setLoading(false);
  };

  const setUp = async () => {
    await fetchResellings({
      page: pagination.page,
      pageSize: pagination.pageSize,
    });
  };

  const handleSubmit = async () => {
    if (!validParams()) {
      return;
    }
    setLoading(true);
    try {
      if (edit) {
        if (!resellData.id) return;
        await updateFactoringReSell(dispatch, resellData.id, {
          rate: parseFloat(resellData.rate),
          rateCost: Number(resellData.rateCost),
          amount: Number(resellData.amount),
          advance: Number(resellData.advance),
          arrearRate: Number(resellData.arrearRate),
          startDate: new Date(resellData.startDate),
          dueDate: new Date(resellData.endDate),
          operationCostRateVariable: parseFloat(
            resellData.operationCostRateVariable || '0',
          ),
          operationCostFixed: resellData.operationCostFixed || 0,
          observation: resellData.observation,
          fundId: resellData.fundId,
        });
        setOriginalResellData(null);
        setOpenNewResell(false);
        showAlert(dispatch, 'success', 'Prórroga actualizada correctamente');
        try {
          await setUp();
        } catch (refreshError) {
          // Error al refrescar la lista, pero la actualización fue exitosa
        }
        return;
      } else {
        await createFactoringReSell(dispatch, {
          ...resellData,
          rate: parseFloat(resellData.rate),
          rateCost: Number(resellData.rateCost),
          endDate: new Date(resellData.endDate),
          startDate: new Date(resellData.startDate),
          operationCostRateVariable: parseFloat(
            resellData.operationCostRateVariable || '0',
          ),
          operationCostFixed: resellData.operationCostFixed,
          fundId: resellData.fundId,
          observation: resellData.observation,
        });
      }
      setOpenNewResell(false);
      showAlert(dispatch, 'success', 'Venta creada correctamente');
      try {
        await setUp();
      } catch (refreshError) {
        // Error al refrescar la lista, pero la creación fue exitosa
      }
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al crear la venta');
    }
    setLoading(false);
  };

  const setUpSuggestions = async () => {
    setLoading(true);
    try {
      const response = await getDocumentsAvailableForReSelling(dispatch);
      setSuggestions(response.documentsProcessed);
    } catch (error) {
      // Error handling
    }
    setLoading(false);
  };

  const setUpConfig = async () => {
    try {
      const response = await getCompanyConfig(
        dispatch,
        'system_operations_fund_forcePaymentDate',
      );
      if (response === '1') {
        setCanForcePaymentDate(true);
      }
    } catch (error) {
      console.error('Error fetching date restrictions config', error);
    }
  };

  const setUpResell = async () => {
    if (edit) {
      setLoading(true);
      try {
        if (!resellData.id) return;
        const response = await getFactoringReSell(dispatch, resellData.id);
        const updatedResellData = {
          ...response.reSell,
          startDate: formatDate(response.reSell.startDate),
          endDate: formatDate(response.reSell.dueDate),
          operationCostFixed: response.reSell.operationCostFixed || 0,
        };
        setResellData(updatedResellData);
        const fundsResponse = await getCompanyFunds(dispatch);
        setFunds(fundsResponse);
        setDocument(response.document);
        setSearched(true);
      } catch (error) {
        // Error handling
      }
      setLoading(false);
    } else {
      await setUpSuggestions();
    }
  };

  const setUpDialog = async () => {
    await setUpResell();
    await setUpConfig();
  };

  const handleRevert = () => {
    if (resellData.FundPayment && resellData.FundPayment.length > 0) {
      showAlert(
        dispatch,
        'warning',
        'Debe eliminar los pagos antes de revertir',
      );
      return;
    }
    setLocalResellToRevert(resellData.id || null);
    setOpenRevert(true);
  };

  const handleDocumentReplace = async (newDocumentId: string) => {
    try {
      await replaceResellDocument(dispatch, resellData.id || '', {
        newDocumentId,
      });

      const updatedResellData = {
        ...resellData,
        documentId: newDocumentId,
      };
      setResellData(updatedResellData);

      const response = await getDocumentWithExtensionData(
        dispatch,
        newDocumentId,
      );
      setDocument(response);

      showAlert(dispatch, 'success', 'Documento reemplazado correctamente');
    } catch (error) {
      console.error('Error replacing document:', error);
      showAlert(dispatch, 'error', 'Error al reemplazar el documento');
      throw error;
    }
  };

  useEffect(() => {
    void setUpDialog();
  }, []);

  // Sync local resell data with context when needed
  useEffect(() => {
    setResellData(convertToResellData(resell!));
  }, [resell]);

  return (
    <div>
      {loading ? (
        <CenteredCircularProgress type="dialog" />
      ) : (
        <div>
          {!searched && !edit && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Autocomplete
                style={{ margin: '8px 0px' }}
                options={suggestions}
                getOptionLabel={(option) =>
                  `${option.folio} | ${option.businessName} | ${option.stakeholderName}`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Busca por folio, cliente o deudor"
                  />
                )}
                onChange={handleChangeDocument}
                fullWidth
              />
              <div style={{ padding: '0px 16px' }}>
                <CustomButton
                  onClick={() => handleSearchData()}
                  color="primary"
                >
                  Seleccionar
                </CustomButton>
              </div>
            </div>
          )}
          {searched && (
            <div>
              {document && (
                <>
                  <DocumentInfoCard
                    document={document}
                    resellData={resellData}
                    canEdit={canEditByStatus && canEditByUser}
                    onEdit={() => setOpenDocumentEditor(true)}
                    resell={originalResell!}
                  />
                  <DocumentFormParameters
                    document={document}
                    resellData={resellData}
                    setResellData={setResellData}
                    canModifiyParams={canModifyParams}
                    funds={funds}
                    canForcePaymentDate={canForcePaymentDate}
                    onToggleEditMode={handleToggleEditMode}
                  />
                </>
              )}
            </div>
          )}

          <DialogActions>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0px',
                gap: '10px',
              }}
            >
              <Typography
                color="error"
                variant="body1"
                fontWeight="bold"
                style={{ cursor: 'pointer' }}
                onClick={() => handleRevert()}
              >
                Eliminar
              </Typography>
              <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
                <CustomButton
                  onClick={() => setOpenNewResell(false)}
                  color="secondary"
                >
                  Salir
                </CustomButton>
                {validatePermissions(user, 'financing_fundSale_edit') &&
                  edit &&
                  resellData.status === 'En revisión' && (
                    <CustomButton
                      onClick={handleToggleEditMode}
                      color="warning"
                    >
                      {canModifyParams ? 'Cancelar' : 'Editar'}
                    </CustomButton>
                  )}
                {!edit ||
                resellData.status === 'En revisión' ||
                (validatePermissions(user, 'financing_fundSale_edit') &&
                  canModifyParams) ? (
                  <CustomButton
                    onClick={() => handleSubmit()}
                    color="primary"
                    disabled={!canModifyParams}
                  >
                    Guardar
                  </CustomButton>
                ) : null}
              </div>
            </div>
          </DialogActions>
        </div>
      )}
      {openRevert && (
        <RevertResell
          open={openRevert}
          setOpen={setOpenRevert}
          resellToRevert={localResellToRevert}
          triggerSearch={triggerSearch}
        />
      )}
      {openDocumentEditor && (
        <DocumentEditor
          open={openDocumentEditor}
          setOpen={setOpenDocumentEditor}
          currentDocument={null}
          onDocumentReplace={handleDocumentReplace}
          canReplace={canEditByStatus && canEditByUser}
          resellId={resellData.id || ''}
        />
      )}
    </div>
  );
};

export default ResellMain;
